import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import { toast } from "sonner";
import type { ApiErrorResponse } from "../types/api.types";
export type { ApiResponse } from "../types/api.types";

let lastErrorToast = { key: "", timestamp: 0 };

const apiService: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL?.replace(/\/$/, "") || "/api",
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

apiService.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  async (error: AxiosError) => {
    const responseData = error.response?.data as ApiErrorResponse | undefined;
    const getBackendMessage = (data: any): string => {
      if (!data) return error.message;
      const message = data.message ?? data.error;
      if (typeof message === "string") {
        return message;
      }
      if (message && typeof message === "object") {
        for (const key of Object.keys(message)) {
          const value = message[key];
          if (Array.isArray(value) && value.length > 0) {
            return String(value[0]);
          }
          if (typeof value === "string") {
            return value;
          }
        }
      }
      return error.message;
    };
    const backendMessage = getBackendMessage(responseData);
    const status = error.response?.status;
    const requestConfig = error.config as
      | (NonNullable<AxiosError["config"]> & { _csrfRetried?: boolean })
      | undefined;

    // CSRF 419 is rejected before the controller runs, so retrying once after
    // refreshing Laravel's XSRF cookie cannot duplicate a completed mutation.
    if (status === 419 && requestConfig && !requestConfig._csrfRetried) {
      requestConfig._csrfRetried = true;
      let tokenRefreshed = false;
      try {
        await apiService.get("/csrf-cookie");
        tokenRefreshed = true;
      } catch {
        // Let the original 419 produce the user-facing message below.
      }
      if (tokenRefreshed) {
        return await apiService.request(requestConfig);
      }
    }

    if (status === 422) {
      return Promise.reject(error);
    }
    const url = error.config?.url || "";
    const now = Date.now();
    const shouldShowToast = !(
      url === lastErrorToast.key && now - lastErrorToast.timestamp < 2000
    );

    if (status === 401) {
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    }

    if (shouldShowToast) {
      lastErrorToast = { key: url, timestamp: now };

      switch (status) {
        case 403:
          toast.warning("Sin permisos", {
            description:
              backendMessage || "No tienes permisos para realizar esta acción",
            position: "top-right",
          });
          break;
        case 404:
          toast.error("No encontrado", {
            description: backendMessage || "El recurso solicitado no existe",
            position: "top-right",
          });
          break;
        case 500:
          toast.error("Error del servidor", {
            description: "Hubo un problema en el servidor. Intenta más tarde.",
            position: "top-right",
          });
          break;
        default:
          if (status && status >= 400 && status !== 401) {
            toast.error("Error", {
              description: backendMessage || "Ocurrió un error inesperado",
              position: "top-right",
            });
          }
      }
    }

    console.error(
      `API Error [${status}] ${url}:`,
      error.response?.data || error.message,
    );

    return Promise.reject(error);
  },
);

export default apiService;
